import { toast } from "sonner";
import { shouldRegisterServiceWorker } from "@/config/tenantPublicSite";

const UPDATE_CHECK_MS = 30 * 1000; // 30 saniye - PWA güncellemelerini personellere hızla ulaştırır
const PERIODIC_UPDATE_MS = 15 * 60 * 1000; // 15 dakikada bir arka plan kontrolü

// 🔁 SONSUZ YENİLEME KORUMASI
// Yenileme kararı localStorage'da tutulur; sayfa yenilendiğinde sıfırlanmaz.
// Safari'de eski bir service worker kaydı kalmışsa iki SW sırayla clients.claim()
// çağırır, her seferinde controllerchange tetiklenir ve sayfa sonsuz döngüye girer.
const RELOAD_MARK_KEY = "sw_reload_at";
const RELOAD_COOLDOWN_MS = 60 * 1000;

// Eski sürümlerde kayıt /service-worker.js adresine yapılıyordu. Aynı kapsamda
// (/) iki kayıt bulunması controllerchange fırtınasına yol açar; temizliyoruz.
const LEGACY_SW_PATHS = ["/service-worker.js"];

function readNumber(key) {
  try {
    return parseInt(localStorage.getItem(key) || "0", 10) || 0;
  } catch {
    return 0;
  }
}

/** Yenileme yakın zamanda yapıldıysa tekrar yenileme (döngü kırıcı). */
function canReload() {
  return Date.now() - readNumber(RELOAD_MARK_KEY) > RELOAD_COOLDOWN_MS;
}

function reloadOnce(reason) {
  if (!canReload()) {
    console.warn(`[SW] Yenileme atlandı (döngü koruması): ${reason}`);
    return;
  }
  try {
    localStorage.setItem(RELOAD_MARK_KEY, Date.now().toString());
  } catch {
    /* localStorage kapalıysa yine de yenile */
  }
  console.log(`[SW] Sayfa yenileniyor: ${reason}`);
  window.location.reload();
}

/** Aynı kapsamdaki eski /service-worker.js kayıtlarını kaldırır. */
async function unregisterLegacyWorkers() {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const reg of registrations) {
      const scriptUrl =
        reg.active?.scriptURL || reg.waiting?.scriptURL || reg.installing?.scriptURL || "";
      if (LEGACY_SW_PATHS.some((p) => scriptUrl.endsWith(p))) {
        console.warn(`[SW] Eski kayıt kaldırılıyor: ${scriptUrl}`);
        await reg.unregister();
      }
    }
  } catch (err) {
    console.warn("[SW] Eski kayıtlar temizlenemedi:", err);
  }
}

export function registerServiceWorker() {
  if (!shouldRegisterServiceWorker()) return;

  window.addEventListener("load", async () => {
    const swUrl = `/sw.js`;
    let lastUpdateCheck = 0;

    // Yeni kaydı yapmadan ÖNCE eski kayıtları temizle
    await unregisterLegacyWorkers();

    // Bu sayfa açıldığında zaten bir kontrolcü var mıydı? Yalnızca varsa ve
    // güncellemeyi biz başlattıysak yenileme yapılır.
    const hadControllerAtLoad = Boolean(navigator.serviceWorker.controller);
    let skipWaitingRequested = false;

    const maybeCheckUpdate = (registration) => {
      const now = Date.now();
      if (now - lastUpdateCheck < UPDATE_CHECK_MS) return;
      lastUpdateCheck = now;
      registration.update().catch(() => {});
    };

    // 🚀 Kontrolcü değişiminde yenileme — yalnızca güncellemeyi biz tetiklediysek.
    // İlk kurulumda clients.claim() de controllerchange üretir; o durumda
    // (sayfa daha önce kontrolsüzdü) yenilemeye gerek yoktur.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!hadControllerAtLoad) {
        console.log("[SW] İlk kurulum devraldı, yenileme gerekmiyor.");
        return;
      }
      if (!skipWaitingRequested) {
        console.log("[SW] Kontrolcü değişti ama güncellemeyi biz istemedik, yenileme yok.");
        return;
      }
      reloadOnce("controllerchange");
    });

    navigator.serviceWorker
      .register(swUrl, { updateViaCache: "none" })
      .then((registration) => {
        console.log("ServiceWorker registration successful with scope: ", registration.scope);

        // Bekleyen güncelleme varsa hemen devreye sok
        if (registration.waiting) {
          console.log("Bekleyen service worker bulundu, güncelleniyor...");
          skipWaitingRequested = true;
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
          if (canReload()) {
            toast.success("OtoManager Güncellendi!", {
              description: "Yeni sürüm yüklendi, sayfa yenileniyor...",
              duration: 3000,
            });
          }
          return;
        }

        // İlk kontrol: yüklemeden birkaç saniye sonra (ana iş parçacığını kilitlemesin)
        setTimeout(() => maybeCheckUpdate(registration), 5000);

        // Sayfa odaklandığında ve düzenli aralıklarla güncellemeleri kontrol et.
        // (Her tıklamada kontrol etmek iOS Safari'de gereksiz SW trafiği üretiyordu.)
        window.addEventListener("focus", () => maybeCheckUpdate(registration));
        setInterval(() => maybeCheckUpdate(registration), PERIODIC_UPDATE_MS);

        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (installingWorker) {
            installingWorker.onstatechange = () => {
              if (installingWorker.state === "installed") {
                if (navigator.serviceWorker.controller) {
                  if (!canReload()) {
                    console.warn("[SW] Yakın zamanda güncellendi, döngüyü önlemek için atlanıyor.");
                    return;
                  }
                  console.log("Yeni PWA sürümü kuruldu, otomatik güncelleniyor...");

                  skipWaitingRequested = true;
                  if (registration.waiting) {
                    registration.waiting.postMessage({ type: "SKIP_WAITING" });
                  }

                  toast.success("OtoManager Güncellendi!", {
                    description: "En son sürüm otomatik olarak yüklendi, sayfa yenileniyor...",
                    duration: 4000,
                  });
                }
              }
            };
          }
        };

        if ("Notification" in window) {
          Notification.requestPermission().then((permission) => {
            if (permission === "granted") {
              console.log("Notification permission granted.");
            }
          });
        }
      })
      .catch((error) => {
        console.error("ServiceWorker registration failed: ", error);
      });
  });
}
